import React from 'react';
import Banner from '../../../composants/Banner/Banner';
import { OperationMS } from '../types';
import { TechNotes } from '../domain/analyse/types';
import { buildSyntheseLines } from '../domain/analyse/synthese';
import { getClassement } from '../domain/analyse/attribution';
import { formatNoteGlobale } from '../domain/analyse/notationTechnique';
import { WEIGHT_TECHNIQUE, WEIGHT_FINANCIERE } from '../domain/analyse/scoring';
import { isOffreMasquee, isExpire, isRefuse, isPending, isAccepte } from '../domain/analyse/statutOffre';
import { formatDateHeure } from '../domain/analyse/dates';
import { getCandidatureDate } from '../domain/analyse/historique';

// Synthese (cartes) : agrege technique + financier. Le retenu est le MIEUX-DISANT par note
// globale (resolveWinnerId, via line.isGagnant). Affichage seul : aucune selection manuelle.
interface OperationAnalyseSyntheseProps {
  operation: OperationMS;
  techNotes: TechNotes;
  iaEnCours?: boolean;
}

const MAX_TECH = formatNoteGlobale(10 * WEIGHT_TECHNIQUE); // "5,50"
const MAX_FIN = formatNoteGlobale(10 * WEIGHT_FINANCIERE); // "4,50"
const PCT_TECH = `${Math.round(WEIGHT_TECHNIQUE * 100)}%`;
const PCT_FIN = `${Math.round(WEIGHT_FINANCIERE * 100)}%`;

// Rappel de la ponderation en vigueur (Prix / Technique). Pourcentages derives des poids du
// scoring -> reste synchro si les poids changent (pas de valeur en dur).
const PONDERATION_MEMO = `Pour mémoire, il est convenu d'appliquer une pondération en vigueur selon la répartition suivante : Note Prix à ${PCT_FIN}, Note Technique à ${PCT_TECH}.`;

const OperationAnalyseSynthese: React.FC<OperationAnalyseSyntheseProps> = ({ operation, techNotes, iaEnCours = false }) => {
  const lines = buildSyntheseLines(operation, techNotes);

  return (
    <>
      <Banner variant="info" message={PONDERATION_MEMO} />

      {iaEnCours ? (
        <div className="opms-sk-synthese" aria-hidden="true">
          <span className="opms-sk opms-sk-line" />
          <span className="opms-sk opms-sk-line" />
          <span className="opms-sk opms-sk-line opms-sk-line--short" />
        </div>
      ) : lines.length === 0 ? (
        <p className="opms__empty">Aucun prestataire à comparer pour cette opération.</p>
      ) : (
        <div className="opms__presta-grid">
          {lines.map((line) => {
        const hidden = isOffreMasquee(line.idStatus);
        const candidatureDate = formatDateHeure(getCandidatureDate(operation.historique, line.commandeId));
        const classement = getClassement(operation, techNotes, line.commandeId);

        return (
          <div
            key={line.id}
            className={[
              'opms__presta-col',
              line.isGagnant ? 'opms__presta-col--best' : '',
              hidden ? 'opms__presta-col--disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {line.isGagnant && <div className="opms__presta-badge opms__presta-badge--ok">Entreprise mieux disante</div>}
            {isExpire(line.idStatus) && <div className="opms__presta-badge">Expiré</div>}
            {isRefuse(line.idStatus) && <div className="opms__presta-badge">Refusé</div>}
            {isPending(line.idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Signature en cours</div>}
            {isAccepte(line.idStatus) && <div className="opms__presta-badge opms__presta-badge--ok">Accepté</div>}

            <div className="opms__presta-header">
              <h3>{line.nom || 'Prestataire'}</h3>
              <span>{hidden ? '-' : candidatureDate ? `Offre reçue le ${candidatureDate}` : 'Offre reçue'}</span>
            </div>

            <div className="opms__synthese-row">
              <div className="opms__field">
                <label>Note technique :</label>
                <div className="opms__input--inactif opms__synthese-cell">
                  {hidden ? '-' : `${formatNoteGlobale(line.noteTechnique)}/10`}
                </div>
              </div>
              <div className="opms__field">
                <label>
                  Note pondérée :<span className="opms__ponderation-badge">{PCT_TECH}</span>
                </label>
                <div className="opms__input--inactif opms__synthese-cell">
                  {hidden ? '-' : (
                    <>
                      {formatNoteGlobale(line.weightedTechnique)}/{MAX_TECH}
                      <span className="opms__note-detail"> ({PCT_TECH} de {formatNoteGlobale(line.noteTechnique)})</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="opms__synthese-row">
              <div className="opms__field">
                <label>Note financière :</label>
                <div className="opms__input--inactif opms__synthese-cell">
                  {hidden ? '-' : `${formatNoteGlobale(line.noteFinanciere)}/10`}
                </div>
              </div>
              <div className="opms__field">
                <label>
                  Note pondérée :<span className="opms__ponderation-badge">{PCT_FIN}</span>
                </label>
                <div className="opms__input--inactif opms__synthese-cell">
                  {hidden ? '-' : (
                    <>
                      {formatNoteGlobale(line.weightedFinanciere)}/{MAX_FIN}
                      <span className="opms__note-detail"> ({PCT_FIN} de {formatNoteGlobale(line.noteFinanciere)})</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="opms__note-globale">
              <h4>Note globale :</h4>
              <div className="opms__note-value">
                {hidden ? '-' : `${formatNoteGlobale(line.noteTotal)} / 10`}
              </div>
              {!hidden && <div className="opms__classement">Entreprise classée n°{classement}</div>}
            </div>
          </div>
        );
          })}
        </div>
      )}
    </>
  );
};

export default OperationAnalyseSynthese;
